import React, { useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useInView } from 'framer-motion';

const RAZONES = [
  {
    icon: 'fa-solid fa-location-dot',
    titulo: 'Somos locales',
    descripcion: 'Equipo afincado en Castellón. Podemos reunirnos en persona, conocemos el mercado local y entendemos las necesidades de los negocios de la zona.',
    color: '#2e8bc0',
  },
  {
    icon: 'fa-solid fa-bolt',
    titulo: 'Rapidez real',
    descripcion: 'Una web estática lista en menos de una semana. Sin excusas ni retrasos. Pactamos los plazos desde el principio y los cumplimos.',
    color: '#f0ad4e',
  },
  {
    icon: 'fa-solid fa-euro-sign',
    titulo: 'Precio justo',
    descripcion: 'Sin costes ocultos ni sorpresas. El presupuesto que acordamos es el precio final. Adaptamos siempre la solución a tu presupuesto real.',
    color: '#5cb85c',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    titulo: 'Soporte continuo',
    descripcion: 'No desaparecemos una vez entregada la web. Tienes soporte por WhatsApp, correo y teléfono. Mantenemos tu web actualizada y segura.',
    color: '#e8702a',
  },
  {
    icon: 'fa-solid fa-mobile-screen-button',
    titulo: '100% adaptada a móvil',
    descripcion: 'Todas nuestras webs funcionan correctamente en móvil, tableta y ordenador. Más del 70% del tráfico proviene del móvil y lo tenemos muy en cuenta.',
    color: '#9b59b6',
  },
  {
    icon: 'fa-solid fa-magnifying-glass',
    titulo: 'SEO desde el principio',
    descripcion: 'Optimizamos cada web para que aparezca en Google desde el primer día: estructura de URLs, velocidad de carga y etiquetas correctamente configuradas.',
    color: '#1abc9c',
  },
];

const PorQueNosotros = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="nosotros">
      <Container>
        <div className="text-center mb-5" ref={ref}>
          <motion.span className="badge-primary mb-3 d-inline-block" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}>
            Por qué elegirnos
          </motion.span>
          <motion.h2 className="section-title" initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.1 }}>
            La diferencia está en los detalles
          </motion.h2>
          <div className="accent-line center" />
          <motion.p className="section-subtitle" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.2 }}>
            No somos una agencia cualquiera. Somos tu equipo de desarrollo web en Castellón.
          </motion.p>
        </div>

        <Row className="g-4">
          {RAZONES.map((razon, i) => (
            <Col lg={4} md={6} key={razon.titulo}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                style={{ height: '100%' }}
              >
                <div
                  style={{
                    height: '100%',
                    background: 'var(--bg-card)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 'var(--radius)',
                    padding: '2rem',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'border-color 0.3s, transform 0.3s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = razon.color + '45'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {/* Franja de color izquierda */}
                  <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: razon.color, opacity: 0.65 }} />

                  {/* Icono de fondo decorativo */}
                  <div style={{
                    position: 'absolute', bottom: '-8px', right: '16px',
                    fontSize: '5rem', lineHeight: 1,
                    color: razon.color, opacity: 0.06,
                    pointerEvents: 'none', userSelect: 'none',
                  }}>
                    <i className={razon.icon} />
                  </div>

                  <div style={{ paddingLeft: '10px', position: 'relative' }}>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'white', marginBottom: '0.6rem', fontFamily: 'var(--font-heading)' }}>
                      {razon.titulo}
                    </h3>
                    <p style={{ color: '#8fa3bf', fontSize: '0.89rem', lineHeight: 1.7, margin: 0 }}>
                      {razon.descripcion}
                    </p>
                  </div>
                </div>
              </motion.div>
            </Col>
          ))}
        </Row>

        {/* Testimonio */}
        <motion.div
          className="text-center mt-5"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9 }}
        >
          <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: '2.5rem',
            maxWidth: '700px',
            margin: '0 auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '1.2rem' }}>
              {[...Array(5)].map((_, i) => (
                <i key={i} className="fa-solid fa-star" style={{ color: '#f0ad4e', fontSize: '1rem' }} />
              ))}
            </div>
            <p style={{ color: '#c8d8ec', fontSize: '1.02rem', fontStyle: 'italic', lineHeight: 1.75, marginBottom: '1.2rem' }}>
              "Web Fácil Castellón transformó nuestra presencia en internet en menos de una semana. El proceso fue muy claro y el resultado superó nuestras expectativas. Ahora recibimos clientes que nos encuentran directamente en Google."
            </p>
            <div style={{ color: '#6a86a8', fontSize: '0.87rem' }}>
              <strong style={{ color: '#45a8e0' }}>Cliente Anónimo</strong> — Clínica Dental, Castellón
            </div>
          </div>
        </motion.div>
      </Container>
    </section>
  );
};

export default PorQueNosotros;
